import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AddCaseButton } from './AddCaseButton';
import { StatusTabs, StatusTabItem } from './StatusTabs';
import { ProjectFilterDropdown, ProjectFilterItem } from './ProjectFilterDropdown';
import { CaseSearchBarBlock } from './CaseSearchBarBlock';
import { PriorityFilterItem } from './PriorityFilter';
import { ArchiveFilterValue } from './ArchiveFilter';
import './curve-theme.css';

type CaseListPanelProps = {
  title?: string;
  onAddCase: () => void;

  statusItems: StatusTabItem[];
  activeStatusId: StatusTabItem['id'];
  onStatusSelect: (id: StatusTabItem['id']) => void;

  projectItems: ProjectFilterItem[];
  selectedProjectId: string;
  onProjectSelect: (id: string) => void;

  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearch?: () => void;

  priorityItems: PriorityFilterItem[];
  priorityValue: string;
  onPriorityChange: (value: string) => void;

  archiveValue: ArchiveFilterValue;
  onArchiveChange: (value: ArchiveFilterValue) => void;

  onMessageSearchOpen?: () => void;
  isMessageSearchOpen?: boolean;

  children: ReactNode;
};

export function CaseListPanel({
  title,
  onAddCase,
  statusItems,
  activeStatusId,
  onStatusSelect,
  projectItems,
  selectedProjectId,
  onProjectSelect,
  searchValue,
  onSearchChange,
  onSearch,
  priorityItems,
  priorityValue,
  onPriorityChange,
  archiveValue,
  onArchiveChange,
  onMessageSearchOpen,
  isMessageSearchOpen,
  children,
}: CaseListPanelProps) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t('CaseList.title');
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        background: 'var(--curve-bg-white, #FFFFFF)',
        overflow: 'hidden',
      }}
    >
      {/* 標題列 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 12px',
          height: '52px',
          minHeight: '52px',
          borderBottom: '1px solid var(--curve-border, #E5E5E5)',
          background: 'var(--curve-bg-white, #FFFFFF)',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 'var(--curve-text-heading, 16px)',
            fontWeight: 700,
            color: 'var(--curve-text-dark, #1E293B)',
            letterSpacing: '-0.01em',
          }}
        >
          {resolvedTitle}
        </span>
        <AddCaseButton onClick={onAddCase} />
      </div>

      {/* 狀態標籤頁 */}
      <div style={{ flexShrink: 0 }}>
        <StatusTabs
          items={statusItems}
          activeId={activeStatusId}
          onSelect={onStatusSelect}
        />
      </div>

      {/* 專案篩選下拉 */}
      <div
        style={{
          padding: '8px 8px 0 8px',
          flexShrink: 0,
        }}
      >
        <ProjectFilterDropdown
          items={projectItems}
          selectedId={selectedProjectId}
          onSelect={onProjectSelect}
        />
      </div>

      {/* 搜尋欄區塊 */}
      <div style={{ flexShrink: 0 }}>
        <CaseSearchBarBlock
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          onSearch={onSearch}
          priorityItems={priorityItems}
          priorityValue={priorityValue}
          onPriorityChange={onPriorityChange}
          archiveValue={archiveValue}
          onArchiveChange={onArchiveChange}
          onMessageSearchOpen={onMessageSearchOpen}
          isMessageSearchOpen={isMessageSearchOpen}
        />
      </div>

      {/* 案件列表（可捲動） */}
      <div
        className="curve-case-list-scroll"
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {children}
      </div>
    </div>
  );
}
